import React from 'react'

import { Calendar, Play, Volume2 } from 'lucide-react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'

interface PlaybackSectionProps {
	scheduleName?: string
	scheduleImage?: string
	isPlaying: boolean
	onChangeVolume?: () => void
	hasActiveBoxAllocation?: boolean
}

export const PlaybackSection: React.FC<PlaybackSectionProps> = ({
	scheduleName,
	scheduleImage,
	isPlaying,
	onChangeVolume,
	hasActiveBoxAllocation = false,
}) => {
	const { t } = useTranslation('zones')
	return (
		<div className="space-y-2 md:space-y-4 mb-6 md:mb-12">
			<h2 className="text-sm font-medium text-muted-foreground md:text-xl md:text-foreground md:font-semibold">
				{t('details.playback.title')}
			</h2>

			<div className="bg-shade rounded-xl border">
				<div className="flex items-center justify-between md:justify-start text-xs md:text-sm text-muted-foreground border-b p-4 font-semibold">
					<Play className="h-4 w-4 mr-1 hidden md:block" />
					{isPlaying
						? t('details.playback.playingNow')
						: t('details.playback.notPlaying')}

					{onChangeVolume && hasActiveBoxAllocation && (
						<div className="md:hidden">
							<Button
								variant="link"
								size="sm"
								onClick={onChangeVolume}
								className="flex flex-row text-muted-foreground hover:text-foreground text-xs pr-0"
							>
								<Volume2 className="h-4 w-4" />
								{t('details.playback.changeVolume')}
							</Button>
						</div>
					)}
				</div>

				<div className="flex items-center gap-4 p-4">
					<div className="h-14 w-14 bg-shade rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
						{scheduleImage ? (
							<img
								src={scheduleImage}
								alt={scheduleName}
								className="h-full w-full object-cover"
							/>
						) : (
							<Calendar className="h-7 w-7 text-muted-foreground " />
						)}
					</div>

					<div className="flex-1 min-w-0">
						<div className="text-sm font-medium truncate">
							{scheduleName || t('details.playback.noSchedule')}
						</div>
					</div>

					{onChangeVolume && hasActiveBoxAllocation && (
						<Button
							variant="link"
							size="sm"
							onClick={onChangeVolume}
							className="hidden md:flex text-muted-foreground hover:text-foreground"
						>
							<Volume2 className="h-4 w-4" />
							{t('details.playback.changeVolume')}
						</Button>
					)}
				</div>
			</div>
		</div>
	)
}
